import { Box, Button, Checkbox, CircularProgress, LinearProgress, MenuItem, Paper, SelectChangeEvent, Skeleton, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { FilterSelectMenu } from '@components/Filter';
import { DataTableCard, DataTableCardSkeleton, DataTableHeader, DataTablePagination, DataTableRow, EmptyState } from './components';
import { useDataTableState } from './hooks';
import { DataTableProps, NestedRowData } from './types';

/**
 * Helper function to get consistent row identifier
 * Prioritizes _id, then id, then falls back to index for non-checkbox scenarios
 */
const getRowIdentifier = (
    row: any,
    index: number,
    checkboxSelection: boolean,
    treeData: boolean
): string | number => {
    if (checkboxSelection || treeData) {
        // For checkbox selection or tree data, always use stable identifier
        return row._id ?? row.id ?? `row-${index}`;
    }
    // For non-checkbox scenarios, can use index
    return index;
};

// Styled components
const StyledTableContainer = styled(TableContainer, {
    shouldForwardProp: (prop) =>
        !['showColumnBorders', 'showRowBorders', 'dense', 'stickyHeader'].includes(prop as string),
})<{
    showColumnBorders?: boolean;
    showRowBorders?: boolean;
    dense?: boolean;
    stickyHeader?: boolean;
}>(({ theme, showColumnBorders, showRowBorders, dense, stickyHeader = true }) => ({
    boxSizing: 'border-box',
    paddingRight: dense ? '8px' : '10px',
    paddingBottom: dense ? '8px' : '10px',
    scrollbarWidth: 'thin',
    scrollbarColor: `${alpha(theme.palette.primary.main, 0.45)} ${alpha(theme.palette.border.main, 0.35)}`,
    scrollbarGutter: 'stable both-edges',
    '&::-webkit-scrollbar': {
        width: dense ? '8px' : '10px',
        height: dense ? '8px' : '10px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: alpha(theme.palette.border.main, 0.3),
        borderRadius: '999px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: alpha(theme.palette.primary.main, 0.45),
        borderRadius: '999px',
        border: `2px solid ${alpha(theme.palette.background.paper, 0.9)}`,
    },
    '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.6),
    },
    '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    '& .MuiTableCell-root': {
        borderLeft: showColumnBorders ? `1px solid ${theme.palette.border.main}` : 'none',
        borderBottom: showRowBorders ? `1px solid ${theme.palette.border.main}` : 'none',
        padding: dense ? '10px 12px' : '12px 16px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '&:first-of-type': {
            borderLeft: 'none',
        },
        // Disable ellipsis for checkbox columns
        '&.MuiTableCell-paddingCheckbox': {
            textOverflow: 'unset',
            whiteSpace: 'normal',
            overflow: 'visible',
            width: '40px',
        },
    },
    '& .MuiTableHead-root': {
        ...(stickyHeader && {
            position: 'sticky',
            top: 0,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }),
        '& .MuiTableCell-root': {
            fontWeight: 600,
            ...(stickyHeader && {
                position: 'sticky',
                top: 0,
                borderBottom: `2px solid ${theme.palette.border.main}`,
                // backgroundColor: theme.palette.grey[50],
            }),
        },
    },
    '& .MuiTableRow-root': {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            color: theme.palette.primary.contrastText,
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
        },
    },
}));

const StyledTable = styled(Table)<{ dense?: boolean }>(({ dense }) => ({
    '& .MuiTableCell-root': {
        padding: dense ? '6px 8px' : '12px 16px',
    },
}));

/**
 * Advanced DataTable component with sorting, resizing, and nested row support
 */
export const DataTable = <T extends Record<string, any> = any>({
    rows,
    columns,
    loading = false,
    sortModel,
    defaultSortModel,
    onSortModelChange,
    columnWidths,
    onColumnWidthsChange,
    expandedRows,
    onExpandedRowsChange,
    selectionModel,
    onSelectionModelChange,
    checkboxSelection = false,
    disableMultipleSelection = false,
    pagination,
    treeData = false,
    getChildren,
    persistColumnWidths = false,
    columnWidthsStorageKey = 'datatable-column-widths',
    noDataComponent,
    loadingComponent,
    rowSkeleton,
    skeletonConfig,
    height,
    maxHeight,
    showColumnBorders = false,
    showRowBorders = true,
    stickyHeader = true,
    getRowProps,
    getCellProps,
    onRowClick,
    onRowDoubleClick,
    className,
    sx,
    dense = true,
    onScrollEnd,
    isLoadingMore = false,
    rowHeight = 50,
    keyboardNavigation = true,
    'aria-label': ariaLabel = 'Data grid',
    'aria-labelledby': ariaLabelledBy,
    collapsible = false,
    collapseAnchorColumn,
    isRowSelectable,
    ...props
}: DataTableProps<T>): React.ReactElement => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Auto-create pagination component from pagination props
    const paginationComponent = useMemo(() => {
        if (!pagination) return null;

        const { currentPage, pageSize, totalRows, onPageChange, onPageSizeChange, pageSizeOptions } = pagination;
        const totalPages = Math.ceil(totalRows / pageSize);

        return (
            <DataTablePagination
                totalPages={totalPages}
                currentPage={currentPage}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                dense={dense}
            />
        );
    }, [pagination, dense]);

    // Internal collapsible rows state
    const [internalExpandedRows, setInternalExpandedRows] = React.useState<Set<string | number>>(new Set());

    const handleToggleRow = React.useCallback((rowId: string) => {
        setInternalExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    }, []);

    const handleToggleAllRows = React.useCallback(() => {
        setInternalExpandedRows(prev => {
            const allRowIds = new Set(rows.map((row: any, index: number) =>
                getRowIdentifier(row, index, checkboxSelection, treeData)
            ));
            const allExpanded = prev.size === rows.length && Array.from(prev).every(id => allRowIds.has(id));
            return allExpanded ? new Set() : allRowIds;
        });
    }, [rows, checkboxSelection, treeData]);

    // Use the custom hook for state management
    const {
        sortedRows,
        currentSortModel,
        currentColumnWidths,
        currentExpandedRows,
        currentSelectionModel,
        handleSort,
        handleDirectSort,
        handleColumnResize,
        handleRowExpand,
        handleRowSelect,
        handleSelectAll,
    } = useDataTableState({
        rows,
        columns,
        sortModel,
        defaultSortModel,
        onSortModelChange,
        columnWidths,
        onColumnWidthsChange,
        expandedRows,
        onExpandedRowsChange,
        selectionModel,
        onSelectionModelChange,
        treeData,
        persistColumnWidths,
        columnWidthsStorageKey,
        checkboxSelection,
    });

    const resetToFirstPageOnSort = useCallback(() => {
        if (!pagination || pagination.currentPage === 1) return;
        pagination.onPageChange(1);
    }, [pagination]);

    const handleSortWithPageReset = useCallback((columnId: string) => {
        handleSort(columnId);
        resetToFirstPageOnSort();
    }, [handleSort, resetToFirstPageOnSort]);

    const handleDirectSortWithPageReset = useCallback((field: string, direction: 'asc' | 'desc' | null) => {
        handleDirectSort(field, direction);
        resetToFirstPageOnSort();
    }, [handleDirectSort, resetToFirstPageOnSort]);

    // Process rows for nested display if treeData is enabled
    const processedRows = useMemo(() => {
        if (!treeData) return sortedRows as T[];

        const flattenRows = (rows: NestedRowData[], level = 0): NestedRowData[] => {
            const result: NestedRowData[] = [];

            for (const row of rows) {
                result.push({ ...row, level });

                if (row.children && currentExpandedRows[row.id] && row.children.length > 0) {
                    result.push(...flattenRows(row.children, level + 1));
                }
            }

            return result;
        };

        return flattenRows(sortedRows as NestedRowData[]);
    }, [sortedRows, treeData, currentExpandedRows]);

    const isLoading = typeof loading === 'boolean' ? loading : loading.loading;

    // Scroll-to-bottom detection for loading more data
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container || !onScrollEnd || isLoadingMore) return;

        const { scrollTop, clientHeight, scrollHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
            onScrollEnd();
        }
    }, [onScrollEnd, isLoadingMore]);

    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container || !onScrollEnd) return;

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll, onScrollEnd]);

    // Handle case where content height is less than container height
    // This ensures loadMore is triggered even when there's no scroll bar
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container || !onScrollEnd || isLoadingMore || isLoading) return;

        const checkContentFit = () => {
            const { scrollHeight, clientHeight } = container;
            // If content doesn't fill container and there's more to load, trigger loadMore
            if (scrollHeight <= clientHeight) {
                onScrollEnd();
            }
        };

        // Check immediately
        checkContentFit();

        // Also check with ResizeObserver for container size changes
        const resizeObserver = new ResizeObserver(() => {
            checkContentFit();
        });

        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [onScrollEnd, isLoadingMore, isLoading, pagination?.pageSize, processedRows.length]);

    // Calculate column width allocation for fixed vs flexible columns
    const { totalFixedWidth, flexibleColumnsCount, columnWidthStyles } = useMemo(() => {
        let fixedWidth = 0;
        let flexibleCount = 0;
        const widthStyles: Record<string, { width?: string; minWidth?: string; flexGrow?: number }> = {};

        // Account for checkbox selection column
        if (checkboxSelection) {
            fixedWidth += 58;
        }

        columns.forEach((column) => {
            const resizedWidth = currentColumnWidths[column.id];

            // Check if column has been resized
            if (typeof resizedWidth === 'number' && Number.isFinite(resizedWidth)) {
                fixedWidth += resizedWidth;
                widthStyles[column.id] = {
                    width: `${resizedWidth}px`,
                    minWidth: `${resizedWidth}px`,
                    flexGrow: 0
                };
                return;
            }

            // Check if column has explicit width defined
            if (typeof column.width === 'number') {
                fixedWidth += column.width;
                widthStyles[column.id] = {
                    width: `${column.width}px`,
                    minWidth: `${column.width}px`,
                    flexGrow: 0
                };
                return;
            }

            // Check if column has minWidth defined (treated as fixed)
            if (typeof column.minWidth === 'number') {
                fixedWidth += column.minWidth;
                widthStyles[column.id] = {
                    width: `${column.minWidth}px`,
                    minWidth: `${column.minWidth}px`,
                    flexGrow: 0
                };
                return;
            }

            // Column is flexible - will fill remaining space
            flexibleCount++;
            widthStyles[column.id] = {
                minWidth: '100px', // Minimum width for flexible columns
                flexGrow: 1
            };
        });

        return {
            totalFixedWidth: fixedWidth,
            flexibleColumnsCount: flexibleCount,
            columnWidthStyles: widthStyles,
        };
    }, [columns, currentColumnWidths, checkboxSelection]);

    // ── Mobile card layout ────────────────────────────────────────────────
    if (isMobile) {
        const isLoadingInitial = isLoading && (!processedRows || processedRows.length === 0);
        const isEmpty = !isLoading && (!processedRows || processedRows.length === 0);

        const selectAllLabel =
            currentSelectionModel.selectedRows.length > 0
                ? `${currentSelectionModel.selectedRows.length} of ${processedRows.length} selected`
                : "Tout sélectionner";

        const sortableColumns = columns.filter((c) => c.sortable);
        const activeSortValue =
            currentSortModel.length > 0 && currentSortModel[0].sort
                ? `${currentSortModel[0].field}:${currentSortModel[0].sort}`
                : '';

        const handleMobileSortChange = (event: SelectChangeEvent<string>) => {
            const val = event.target.value;
            if (!val) {
                handleDirectSortWithPageReset('', null);
            } else {
                const [field, dir] = val.split(':');
                handleDirectSortWithPageReset(field, dir as 'asc' | 'desc');
            }
        };

        return (
            <Box
                className={className}
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    ...sx,
                }}
            >
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}>
                    {/* Select-all bar */}
                    {checkboxSelection && !isEmpty && !isLoadingInitial && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.5,
                                py: 0.25,
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={currentSelectionModel.isAllSelected}
                                indeterminate={
                                    currentSelectionModel.isSomeSelected &&
                                    !currentSelectionModel.isAllSelected
                                }
                                onChange={() => handleSelectAll()}
                                sx={{ p: 0.5 }}
                            />
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                {selectAllLabel}
                            </Typography>
                        </Box>
                    )}

                    {/* Expand / collapse all (collapsible mode only) */}
                    {collapsible && !isEmpty && !isLoadingInitial && (
                        <Tooltip title={internalExpandedRows.size === processedRows.length ? 'Tout réduire' : 'Tout développer'}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleToggleAllRows}
                                sx={{ textTransform: 'none', color: theme.palette.text.secondary, minWidth: 0 }}
                            >
                                {internalExpandedRows.size === processedRows.length ? 'Réduire tout' : 'Développer tout'}
                            </Button>
                        </Tooltip>
                    )}

                    {/* Mobile sort select */}
                    {sortableColumns.length > 0 && !isEmpty && !isLoadingInitial && (
                        <FilterSelectMenu
                            size="small"
                            value={activeSortValue}
                            onChange={handleMobileSortChange as any}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>Aucun tri</em>
                            </MenuItem>
                            {sortableColumns.flatMap((col) => [
                                <MenuItem key={`${col.id}:asc`} value={`${col.id}:asc`}>
                                    {typeof col.label === 'string' ? col.label : col.id} ↑
                                </MenuItem>,
                                <MenuItem key={`${col.id}:desc`} value={`${col.id}:desc`}>
                                    {typeof col.label === 'string' ? col.label : col.id} ↓
                                </MenuItem>,
                            ])}
                        </FilterSelectMenu>
                    )}
                </Box>
                {/* Loading state */}
                {isLoadingInitial && (
                    <DataTableCardSkeleton
                        count={skeletonConfig?.rowCount ?? 5}
                        checkboxSelection={checkboxSelection}
                        dense={dense}
                    />
                )}

                {/* Empty state */}
                {isEmpty &&
                    (noDataComponent
                        ? (noDataComponent as React.ReactElement)
                        : <EmptyState />)}

                {/* Card list */}
                {!isLoadingInitial && !isEmpty && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {processedRows.map((row, index) => (
                            <DataTableCard
                                key={
                                    (row as any)._id ??
                                    (row as any).id ??
                                    index
                                }
                                row={row as T}
                                columns={columns}
                                rowIndex={index}
                                selectionModel={currentSelectionModel}
                                onRowSelect={handleRowSelect}
                                checkboxSelection={checkboxSelection}
                                isRowSelectable={isRowSelectable}
                                onRowClick={onRowClick}
                                onRowDoubleClick={onRowDoubleClick}
                                getCellProps={getCellProps}
                                dense={dense}
                                collapsible={collapsible}
                                collapseAnchorColumn={collapseAnchorColumn}
                                collapsibleExpandedRows={internalExpandedRows}
                                onToggleCollapsibleRow={handleToggleRow}
                            />
                        ))}

                        {/* Load-more skeleton cards */}
                        {isLoadingMore && (
                            <DataTableCardSkeleton
                                count={3}
                                checkboxSelection={checkboxSelection}
                                dense={dense}
                            />
                        )}
                    </Box>
                )}

                {/* Pagination */}
                {paginationComponent && <Box>{paginationComponent}</Box>}
            </Box>
        );
    }
    // ── End mobile card layout ────────────────────────────────────────────

    if (isLoading && (!processedRows || processedRows.length === 0)) {
        return (
            <Box
                className={className}
                sx={{
                    height: height || (stickyHeader ? '400px' : 'auto'),
                    maxHeight,
                    width: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    ...sx,
                }}
            >
                <StyledTableContainer
                    showColumnBorders={showColumnBorders}
                    showRowBorders={showRowBorders}
                    dense={dense}
                    stickyHeader={stickyHeader}
                    sx={{
                        flex: 1,
                        overflowX: 'auto',
                        overflowY: 'auto',
                    }}
                >
                    <StyledTable
                        dense={dense}
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledBy}
                        size={dense ? 'small' : 'medium'}
                        sx={{
                            width: '100%',
                            minWidth: totalFixedWidth > 0 ? `${totalFixedWidth}px` : '100%',
                            tableLayout: flexibleColumnsCount > 0 ? 'auto' : 'fixed',
                        }}
                    >
                        <DataTableHeader
                            columns={columns}
                            sortModel={currentSortModel}
                            onSort={handleSortWithPageReset}
                            columnWidths={currentColumnWidths}
                            columnWidthStyles={columnWidthStyles}
                            onColumnResize={handleColumnResize}
                            selectionModel={currentSelectionModel}
                            onSelectAll={handleSelectAll}
                            checkboxSelection={checkboxSelection}
                            disableMultipleSelection={disableMultipleSelection}
                            stickyHeader={stickyHeader}
                            dense={dense}
                            rowHeight={rowHeight}
                            collapsible={collapsible}
                            collapseAnchorColumn={collapseAnchorColumn}
                            expandedRows={internalExpandedRows}
                            onToggleAllRows={handleToggleAllRows}
                            totalRows={rows.length}
                        />
                        <TableBody>
                            {rowSkeleton ? (
                                Array.from(new Array(10)).map((_, rowIndex) => (
                                    <React.Fragment key={`skeleton-row-${rowIndex}`}>
                                        {rowSkeleton}
                                    </React.Fragment>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length + (checkboxSelection ? 1 : 0)}
                                        sx={{
                                            textAlign: 'center',
                                            py: 2,
                                            border: 'none',
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>

                {/* Pagination component skeleton */}
                {pagination && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                        <Skeleton variant="rectangular" width={300} height={36} />
                    </Box>
                )}
            </Box>
        );
    }
    // Handle empty state
    if (!isLoading && (!processedRows || processedRows.length === 0)) {
        const EmptyComponent = noDataComponent as React.ReactElement || <EmptyState />;
        return EmptyComponent;
    }

    return (
        <Box
            className={className}
            sx={{
                height: height || (stickyHeader ? '400px' : 'auto'), // Default height when sticky header is enabled
                maxHeight,
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '0',
                position: 'relative',
                ...sx,
            }}
        >
            {/* Progress Bar */}
            {
                isLoadingMore && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 32,
                            left: 0,
                            width: 'calc(100% - 8px)',
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1,
                            zIndex: 104,
                        }}
                    >
                        <LinearProgress sx={{ width: '100%' }} />
                    </Box>
                )
            }
            <StyledTableContainer
                ref={tableContainerRef}
                showColumnBorders={showColumnBorders}
                showRowBorders={showRowBorders}
                dense={dense}
                stickyHeader={stickyHeader}
                sx={{
                    flex: 1,
                    overflowX: 'auto', // Ensure horizontal scrolling is enabled
                    overflowY: 'auto', // Ensure vertical scrolling is enabled
                }}
            >
                <StyledTable
                    dense={dense}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    size={dense ? 'small' : 'medium'}
                    sx={{
                        // Always fill container width, but set minWidth based on fixed columns
                        width: '100%',
                        minWidth: totalFixedWidth > 0 ? `${totalFixedWidth}px` : '100%',
                        tableLayout: flexibleColumnsCount > 0 ? 'auto' : 'fixed',
                    }}
                >
                    <DataTableHeader
                        columns={columns}
                        sortModel={currentSortModel}
                        onSort={handleSortWithPageReset}
                        columnWidths={currentColumnWidths}
                        columnWidthStyles={columnWidthStyles}
                        onColumnResize={handleColumnResize}
                        selectionModel={currentSelectionModel}
                        onSelectAll={handleSelectAll}
                        checkboxSelection={checkboxSelection}
                        disableMultipleSelection={disableMultipleSelection}
                        stickyHeader={stickyHeader}
                        dense={dense}
                        rowHeight={rowHeight}
                        collapsible={collapsible}
                        collapseAnchorColumn={collapseAnchorColumn}
                        expandedRows={internalExpandedRows}
                        onToggleAllRows={handleToggleAllRows}
                        totalRows={rows.length}
                    />
                    <TableBody>
                        {processedRows.map((row, index) => (
                            <DataTableRow
                                key={getRowIdentifier(row, index, checkboxSelection, treeData)}
                                row={row}
                                columns={columns}
                                rowIndex={index}
                                columnWidths={currentColumnWidths}
                                columnWidthStyles={columnWidthStyles}
                                selectionModel={currentSelectionModel}
                                expandedRows={currentExpandedRows}
                                onRowSelect={handleRowSelect}
                                onRowExpand={handleRowExpand}
                                onRowClick={onRowClick}
                                onRowDoubleClick={onRowDoubleClick}
                                getRowProps={getRowProps}
                                getCellProps={getCellProps}
                                checkboxSelection={checkboxSelection}
                                treeData={treeData}
                                dense={dense}
                                rowHeight={rowHeight}
                                keyboardNavigation={keyboardNavigation}
                                isLoading={isLoading}
                                collapsible={collapsible}
                                collapseAnchorColumn={collapseAnchorColumn}
                                collapsibleExpandedRows={internalExpandedRows}
                                onToggleCollapsibleRow={handleToggleRow}
                                isRowSelectable={isRowSelectable}
                            />
                        ))}
                        {isLoadingMore && rowSkeleton && (
                            <>
                                {
                                    Array.from(new Array(10)).map((_, rowIndex) => (
                                        <React.Fragment key={`skeleton-row-${rowIndex}`}>
                                            {rowSkeleton}
                                        </React.Fragment>
                                    ))
                                }   
                            </>
                        )}
                    </TableBody>
                </StyledTable>
            </StyledTableContainer>

            {/* Pagination component */}
            {paginationComponent && (
                <Box>
                    {paginationComponent}
                </Box>
            )}
        </Box>
    );
};

// Default export
export default DataTable;
